<script setup lang="tsx">
import { watch } from 'vue'
import RuleRow from '@/components/rules/RuleRow.vue'
import methodEditor from '@/stores/method-editor'

import type { StandardMethod, StandardMethodMetaData } from '@/models/api-generated'
import {
	ConditionallyImplemented,
	PartiallyImplemented,
	RuleWillNotApply,
	RuleWillNotImplement,
	type RuleStatus,
} from '@/models/enums-generated'
import { promptForRequiredComment } from '../methods/required-comment'
import { getRuleStatusFromMethod } from '@/services/utils'

const props = defineProps<{
	method: StandardMethod
	isReadOnly: boolean
}>()
const model = defineModel<StandardMethodMetaData>({ required: true })
const editor = methodEditor()

watch(
	() => model.value.status,
	(newStatus) => {
		if (!editor.isEditMode) return
		if (newStatus == ConditionallyImplemented || newStatus == PartiallyImplemented) return
		setStatus(getRuleStatusFromMethod(newStatus))
	},
)
function setStatus(status: RuleStatus) {
	let cmt: string | null | undefined
	if ([RuleWillNotApply, RuleWillNotImplement].includes(status)) {
		cmt = promptForRequiredComment()
		if (cmt) model.value.comment = cmt
	}

	for (let i = 0; i < model.value.rules.length; i++) {
		model.value.rules[i].status = status
		if (cmt) model.value.rules[i].comment = cmt

		for (let j = 0; j < model.value.rules[i].sub_rules.length; j++) {
			model.value.rules[i].sub_rules[j].status = status
			if (cmt) model.value.rules[i].sub_rules[j].comment = cmt
		}
	}
}
</script>

<template>
	<div class="row text-center fw-bold">
		<div class="col">{{ $t('rule.label') }}</div>
		<div class="col-3">{{ $t('status.label') }}</div>
	</div>

	<template v-for="(rule, i) in model.rules" :key="rule.id">
		<RuleRow v-model="model.rules[i]" :is-read-only="props.isReadOnly"></RuleRow>

		<div v-if="props.method.oldRuleMap[rule.id]" class="mt-3 eits-rule-migration">
			<h6 class="mx-2">E-ITS {{ props.method.oldRuleMap[rule.id].rule.version }}</h6>
			<RuleRow v-model="props.method.oldRuleMap[rule.id]" :is-read-only="true"></RuleRow>
		</div>
		<hr v-if="i < model.rules.length - 1" />
	</template>

	<div v-if="props.method.oldRuleAll.length > 0" class="mt-3 eits-rule-migration">
		<h6 class="mx-2">E-ITS {{ props.method.oldRuleAll[0].rule.version }}</h6>
		<template v-for="(oldRule, i) in props.method.oldRuleAll" :key="oldRule.id">
			<RuleRow v-model="props.method.oldRuleAll[i]" :is-read-only="true"> </RuleRow>
			<hr v-if="i < props.method.oldRuleAll.length - 1" />
		</template>
	</div>
</template>

<style lang="sass" scoped>
.eits-rule-migration
	margin-left: 0
	padding: 1rem 0.5rem
	border-radius: 5px
	background-color: var(--bs-primary-bg-subtle)

	h6
		color: var(--bs-body-color)
		margin-bottom: 1rem

	.rule-comment-info
		background-color: var(--bs-primary)

	.updated
		margin-left: 1rem
</style>
